import React from 'react';
import { useAuth } from '../auth/AuthContext';
import { ProfileScreen } from '../auth/screens/ProfileScreen';
import './producer.css';
import { AccountIcon, DealsIcon, HomeIcon, LogoutIcon } from './icons';
import { Home } from './Home';
import { Deals } from './Deals';
import { DealView } from './DealView';
import type { DealTab } from './DealView';

type Route =
  | { name: 'home' }
  | { name: 'deals' }
  | { name: 'deal'; id: string; tab: DealTab }
  | { name: 'account' };

const TABS: DealTab[] = ['overview', 'animals', 'health', 'documents'];

function parse(hash: string): Route {
  const parts = hash.replace(/^#\/?/, '').split('/').filter(Boolean);
  if (parts[0] === 'deals' && parts[1]) {
    const tab = TABS.includes(parts[2] as DealTab) ? (parts[2] as DealTab) : 'overview';
    return { name: 'deal', id: parts[1], tab };
  }
  if (parts[0] === 'deals') return { name: 'deals' };
  if (parts[0] === 'account') return { name: 'account' };
  return { name: 'home' };
}

/** Hash routing keeps the phone's back button working with no router dependency. */
function useRoute() {
  const [route, setRoute] = React.useState<Route>(() => parse(window.location.hash));
  React.useEffect(() => {
    const on = () => { setRoute(parse(window.location.hash)); window.scrollTo(0, 0); };
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  const go = React.useCallback((to: string) => { window.location.hash = to; }, []);
  const replace = React.useCallback((to: string) => {
    window.history.replaceState(null, '', `#${to}`);
    setRoute(parse(`#${to}`));
  }, []);
  return { route, go, replace };
}

export const ProducerApp: React.FC = () => {
  const { user, logout } = useAuth();
  const { route, go, replace } = useRoute();

  React.useEffect(() => {
    document.body.classList.add('regular-user-body');
    return () => document.body.classList.remove('regular-user-body');
  }, []);

  const firstName =
    (user as any)?.first_name ||
    (user?.username && user.username.includes('.')
      ? user.username.split('.').pop()!.replace(/^./, c => c.toUpperCase())
      : user?.username) ||
    'there';

  const active = route.name === 'deal' ? 'deals' : route.name;

  return (
    <div className="pf">
      {route.name === 'home' && (
        <Home firstName={firstName} onOpenDeal={id => go(`/deals/${id}`)} onAllDeals={() => go('/deals')} />
      )}
      {route.name === 'deals' && <Deals onOpenDeal={id => go(`/deals/${id}`)} />}
      {route.name === 'deal' && (
        <DealView id={route.id} tab={route.tab}
          onTab={t => replace(`/deals/${route.id}/${t}`)}
          onBack={() => go('/deals')} />
      )}
      {route.name === 'account' && (
        <div className="pf-screen">
          <header className="pf-top"><div className="pf-wrap">
            <h1 className="pf-h1">Your account</h1>
            <p className="pf-sub" style={{ marginTop: 4 }}>{(user as any)?.email || user?.username}</p>
          </div></header>
          <main className="pf-wrap pf-stack" style={{ paddingTop: 20 }}>
            <ProfileScreen />
            <button type="button" className="pf-btn is-ghost is-block" onClick={() => logout()}>
              <LogoutIcon size={20} /> Log out
            </button>
          </main>
        </div>
      )}

      <nav className="pf-nav" aria-label="Main">
        <div className="pf-nav-in">
          <button type="button" aria-current={active === 'home' ? 'page' : undefined} onClick={() => go('/')}>
            <HomeIcon /><span>Home</span>
          </button>
          <button type="button" aria-current={active === 'deals' ? 'page' : undefined} onClick={() => go('/deals')}>
            <DealsIcon /><span>Deals</span>
          </button>
          <button type="button" aria-current={active === 'account' ? 'page' : undefined} onClick={() => go('/account')}>
            <AccountIcon /><span>Account</span>
          </button>
        </div>
      </nav>
    </div>
  );
};
